import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Check, ChevronRight, Eye, Palette, Sparkles, 
  Minimize2, ShoppingBag, Building2, ArrowLeft,
  Crown, Star, Zap, HeadphonesIcon, Paintbrush,
  Globe, BarChart3, MessageCircle
} from 'lucide-react';
import { getStandardTemplates, getPremiumTemplate } from '../config/partnerTemplates';
import Header from '../components/Header';

const TemplatePreviewPage = () => {
  const navigate = useNavigate();
  const standardTemplates = getStandardTemplates();
  const premiumTemplate = getPremiumTemplate();
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const getTemplateIcon = (id) => {
    switch (id) {
      case 'classic': return <Palette className="w-6 h-6" />;
      case 'modern': return <Sparkles className="w-6 h-6" />;
      case 'minimal': return <Minimize2 className="w-6 h-6" />;
      case 'showcase': return <ShoppingBag className="w-6 h-6" />;
      case 'corporate': return <Building2 className="w-6 h-6" />;
      case 'premium': return <Crown className="w-6 h-6" />;
      default: return <Palette className="w-6 h-6" />;
    }
  };

  const getPreviewGradient = (template) => {
    if (!template.styles) return 'bg-gradient-to-br from-amber-500 via-orange-500 to-red-500';
    return template.styles.heroBg;
  };

  const handlePremiumContact = () => {
    // Open WhatsApp or contact form
    window.open('https://wa.me/5563992443891?text=Olá! Tenho interesse no Site Premium para parceiros. Gostaria de saber mais sobre valores e personalização.', '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-6">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="w-5 h-5" />
              Voltar
            </button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Templates de Site para Parceiros
            </h1>
            <p className="text-gray-600">
              Escolha o visual ideal para sua página. Templates padrão inclusos no plano ou opte pelo Premium para personalização total.
            </p>
          </div>

          {/* Premium Banner */}
          <div 
            onClick={() => setShowPremiumModal(true)}
            className="relative mb-10 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-2xl p-8 text-white cursor-pointer hover:shadow-2xl transition-all duration-300 overflow-hidden group"
          >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>

            <div className="relative flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
                  <Crown className="w-10 h-10 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                      Exclusivo
                    </span>
                    <Star className="w-5 h-5 fill-white" />
                    <Star className="w-5 h-5 fill-white" />
                    <Star className="w-5 h-5 fill-white" />
                  </div>
                  <h2 className="text-3xl font-bold mb-1">Site Premium Personalizado</h2>
                  <p className="text-white/90 text-lg">
                    Design 100% exclusivo com as cores da sua marca e suporte dedicado
                  </p>
                </div>
              </div>

              <button className="flex items-center gap-2 px-8 py-4 bg-white text-orange-600 font-bold rounded-xl hover:bg-gray-100 transition-colors shadow-lg group-hover:scale-105 transition-transform">
                <span>Quero Saber Mais</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Feature pills */}
            <div className="relative flex flex-wrap gap-3 mt-6">
              <span className="px-4 py-2 bg-white/10 backdrop-blur rounded-lg text-sm flex items-center gap-2">
                <Paintbrush className="w-4 h-4" /> Design Exclusivo
              </span>
              <span className="px-4 py-2 bg-white/10 backdrop-blur rounded-lg text-sm flex items-center gap-2">
                <Globe className="w-4 h-4" /> Domínio Próprio
              </span>
              <span className="px-4 py-2 bg-white/10 backdrop-blur rounded-lg text-sm flex items-center gap-2">
                <HeadphonesIcon className="w-4 h-4" /> Suporte Prioritário
              </span>
              <span className="px-4 py-2 bg-white/10 backdrop-blur rounded-lg text-sm flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> Relatórios Avançados
              </span>
            </div>
          </div>

          {/* Standard Templates Section */}
          <div className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Templates Padrão</h2>
            <p className="text-gray-500 text-sm">Inclusos em todos os planos de parceiro</p>
          </div>

          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {standardTemplates.map((template) => (
              <div
                key={template.id}
                onClick={() => setSelectedTemplate(template.id)}
                className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border-2 ${
                  selectedTemplate === template.id 
                    ? 'border-blue-500 ring-4 ring-blue-100' 
                    : 'border-transparent hover:border-gray-200'
                }`}
              >
                {/* Preview Header */}
                <div className={`${getPreviewGradient(template)} h-32 relative`}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center">
                      {getTemplateIcon(template.id)}
                    </div>
                  </div>
                  {selectedTemplate === template.id && (
                    <div className="absolute top-3 right-3 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                  )}
                  {/* Price tag */}
                  <div className="absolute top-3 left-3 px-3 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                    {template.price}
                  </div>
                </div>

                {/* Info */}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {template.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {template.description}
                  </p>

                  {/* Preview Swatches */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs text-gray-400">Cores:</span>
                    <div className={`w-6 h-6 rounded-full ${template.styles.heroBg.split(' ')[0]}`} />
                    <div className={`w-6 h-6 rounded-full bg-green-500`} />
                    <div className={`w-6 h-6 rounded-full bg-gray-200`} />
                  </div>

                  {/* Preview Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/parceiros/rocha?template=${template.id}`);
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Visualizar
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Selected Template Details */}
          {selectedTemplate && (
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Template Selecionado: {standardTemplates.find(t => t.id === selectedTemplate)?.name}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Features */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-4">Características:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500" />
                      <span>Hero section com logo e informações</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500" />
                      <span>Botão WhatsApp integrado</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500" />
                      <span>Seção "Sobre a Empresa"</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500" />
                      <span>Catálogo de produtos com busca</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500" />
                      <span>Modal de contatos</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500" />
                      <span>Responsivo para mobile</span>
                    </li>
                  </ul>
                </div>

                {/* Ideal For */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-4">Ideal para:</h3>
                  <div className="space-y-3">
                    {selectedTemplate === 'classic' && (
                      <>
                        <p className="text-gray-600">• Empresas tradicionais</p>
                        <p className="text-gray-600">• Assistências técnicas</p>
                        <p className="text-gray-600">• Distribuidores</p>
                      </>
                    )}
                    {selectedTemplate === 'modern' && (
                      <>
                        <p className="text-gray-600">• Startups e tech</p>
                        <p className="text-gray-600">• Produtos inovadores</p>
                        <p className="text-gray-600">• Público jovem</p>
                      </>
                    )}
                    {selectedTemplate === 'minimal' && (
                      <>
                        <p className="text-gray-600">• Lojas premium</p>
                        <p className="text-gray-600">• Design/arquitetura</p>
                        <p className="text-gray-600">• Produtos exclusivos</p>
                      </>
                    )}
                    {selectedTemplate === 'showcase' && (
                      <>
                        <p className="text-gray-600">• E-commerce</p>
                        <p className="text-gray-600">• Varejo</p>
                        <p className="text-gray-600">• Muitos produtos</p>
                      </>
                    )}
                    {selectedTemplate === 'corporate' && (
                      <>
                        <p className="text-gray-600">• B2B</p>
                        <p className="text-gray-600">• Indústrias</p>
                        <p className="text-gray-600">• Empresas grandes</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className="mt-8 pt-6 border-t flex justify-end gap-4">
                <button
                  onClick={() => navigate(`/parceiros/rocha?template=${selectedTemplate}`)}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors"
                >
                  Ver Preview Completo
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Premium Modal */}
      {showPremiumModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-8 text-white relative overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              </div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <Crown className="w-10 h-10" />
                  <div>
                    <h2 className="text-2xl font-bold">Site Premium</h2>
                    <p className="text-white/80">Personalização Total</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPremiumModal(false)}
                  className="absolute top-0 right-0 text-white/80 hover:text-white"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                O que está incluso no Site Premium:
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {premiumTemplate.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Como funciona:</h4>
                <ol className="space-y-3 text-gray-600">
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
                    <span>Entre em contato conosco para uma consultoria inicial</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
                    <span>Definimos juntos o visual, cores e funcionalidades</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
                    <span>Nossa equipe desenvolve seu site exclusivo</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">4</span>
                    <span>Você aprova e publicamos com suporte contínuo</span>
                  </li>
                </ol>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handlePremiumContact}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold rounded-xl transition-all shadow-lg"
                >
                  <MessageCircle className="w-5 h-5" />
                  Falar no WhatsApp
                </button>
                <button
                  onClick={() => setShowPremiumModal(false)}
                  className="flex-1 px-6 py-4 border-2 border-gray-200 hover:border-gray-300 text-gray-700 font-medium rounded-xl transition-colors"
                >
                  Ver Templates Padrão
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatePreviewPage;
